import React, { useState, useEffect } from "react";
import { Box, Typography, Stack, Button } from "@mui/material";
import Grid from "@mui/material/Grid"; // ✅ Grid v2 (supports `size`)
import { motion, AnimatePresence } from "framer-motion";
import Youtube from "react-youtube";
import parse from "html-react-parser";

// Data is separated from the component logic
const featureData = [
  {
    id: 0,
    tag: "Why Wrightglazing?",
    title: "WHY WRIGHTGLAZING?",
    description: parse(
      `<p>We are now able to offer you the ultimate luxury in outdoor living solutions, designed for people that want to enjoy their outdoor space whatever the weather.</p>
       <p>Giving you the flexibility to enjoy full sun, partial shade or complete shelter, these beautiful opening and closing louvered roof pergolas are an adaptable all-weather solution which can be provided with optional windproof screens or sliding panels, integrated LED lighting and instant infra-red heaters, ensuring the perfect all-season environment for your patio or terrace area.</p>
       <p>Manufactured here in the UK, no other canopy can offer the same flexibility as these original louvered pergolas.</p>`
    ),
  },
  {
    id: 1,
    tag: "Pergola Features",
    title: "PERGOLA FEATURES",
    description: parse(
      `<ul>
         <li>Roof louvers rotate up to 150 degrees providing flexible shade</li>
         <li>When closed the roof is completely water, wind and snow tight</li>
         <li>Constructed from superstrong, re-enforced extruded aluminum for durability</li>
         <li>Hardware powder coated to marine grade finish for longevity</li>
         <li>Sleek contemporary design with no visible fixings</li>
         <li>Integrated guttering system ensuring effective water drainage from the roof</li>
         <li>Concealed motors for effortless functioning operated by remote control</li>
       </ul>`
    ),
  },
  {
    id: 2,
    tag: "Extras",
    title: "EXTRAS",
    description: parse(
      `<ul>
         <li>Windproof side screens offering additional protection from the elements</li>
         <li>Dimmable LED lighting within the roof louvers and cross beams</li>
         <li>Infra-red heaters mounted to the cross beams for instant warmth</li>
       </ul>`
    ),
  },
];

export default function VideoTimelapseFeature() {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % featureData.length);
    }, 7000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const activeContent = featureData.find((item) => item.id === activeIndex);

  return (
    <Box
      sx={{
        // maxHeight: {md: "100vh"},
        backgroundColor: "#1a1a1a",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        p: { xs: 2, md: 6 },
        position: "relative",
      }}
    >
      <Grid
        container
        columns={12}
        columnSpacing={4}
        rowSpacing={4}
        alignItems="center"
        sx={{
          zIndex: 1,
          width: "100%",        // ✅ fill the flex parent width
          flex: "1 1 100%",     // ✅ don't shrink inside the flex parent
          "& > *": { minWidth: 0 }, // ✅ children can't force width
        }}
      >
        {/* LEFT: fixed half on md+ */}
        <Grid item size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
          <Box pb={2} sx={{ textAlign: "center" }}>
            <Typography variant="h4" color="#FFF" fontWeight="bold" letterSpacing={5}>
              ALL-WEATHER LOURVRED PERGOLAS
            </Typography>
          </Box>

          <Stack spacing={4} sx={{ flex: 1, width: "100%" }}>
            {/* Interactive Controls */}
            <Stack direction="row" spacing={1}>
              {featureData.map((item) => (
                <Button
                  key={item.id}
                  onClick={() => setActiveIndex(item.id)}
                  sx={{
                    flex: 1,
                    color: "white",
                    py: 1,
                    backgroundColor:
                      activeIndex === item.id
                        ? "rgb(22, 161, 216)"
                        : "rgba(255, 255, 255, 0.1)",
                    "&:hover": {
                      backgroundColor:
                        activeIndex === item.id
                          ? "rgb(22, 161, 216)"
                          : "rgba(255, 255, 255, 0.2)",
                    },
                  }}
                >
                  {item.tag}
                </Button>
              ))}
            </Stack>

            <Box
              sx={{
                backgroundColor: "rgba(0,0,0,0.3)",
                borderRadius: 2,
                p: 4,
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                display: "grid",           // <-- key: grid stacks children
              }}
            >
              {featureData.map((item) => {
                const isActive = item.id === activeIndex;
                return (
                  <motion.div
                    key={item.id}
                    style={{ gridArea: "1 / 1" }}  // <-- stack all in same cell
                    initial={{ opacity: isActive ? 0 : 0 }}
                    animate={{ opacity: isActive ? 1 : 0 }}
                    transition={{ duration: 0.4 }}
                    aria-hidden={!isActive}
                  >
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      color="rgb(22, 161, 216)"
                      gutterBottom
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="rgba(255, 255, 255, 0.8)"
                      sx={{ lineHeight: 1.7 }}
                    >
                      {item.description}
                    </Typography>
                  </motion.div>
                );
              })}
            </Box>
          </Stack>
        </Grid>

        {/* RIGHT: fixed half on md+ */}
        <Grid item size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: "100%",
              aspectRatio: "16 / 9",
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              minWidth: 0, // ✅ extra guard
            }}
          >
            <Youtube
              videoId="FFFs4y99hf4"
              style={{ width: "100%", height: "100%", display: "block" }} // ✅ block prevents intrinsic min-width quirks
              opts={{
                width: "100%",
                height: "100%",
                playerVars: {
                  autoplay: 1,
                  mute: 1,
                  loop: 1,
                  playlist: "FFFs4y99hf4",
                  controls: 0,
                  showinfo: 0,
                  modestbranding: 1,
                },
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
